/**
 * 品味誌 — 公開深度頁(不依賴 JWT,類似 BrandFounder / BrandEvent / BrandNaming)
 *
 * 從 LINE「霽雲・知遇」入口卡的「🍃 品味誌」entry → reply tasting_notes bubble →
 * 底部按鈕跳這頁,by slug。
 *
 * route: /brand/tasting/:slug
 * 目前內容:longan-jujube(桂圓紅棗燕窩 → 霽雲・恆時即飲)
 * 未來新口味直接 push entry 到 TASTING_NOTES dict。
 */
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";


const GCS_BASE = "https://storage.googleapis.com/yannwo-product-images/brand";

type TastingNote = {
  imageKey: string;            // hero GCS key(用 3:4 portrait 版本)
  titleZh: string;
  subtitle: string;            // 副標(一盞溫養 · 把珍貴喝成日常)
  lead: string;                // 珊瑚橘邊條 quote
  intro: string[];             // 開場 1-2 段
  flavor: string[];            // 風味描述段
  closing: string;             // 收尾品牌標語
  relatedProduct?: {
    name: string;
    spec: string;
    note?: string;
    productId?: string;  // 對應商品 SKU id,有給就讓卡可點跳 /products#product-{id} 自動 scroll + highlight
  };
  tags?: string[];
  gallery?: string[];
};

const TASTING_NOTES: Record<string, TastingNote> = {
  "longan-jujube": {
    imageKey: "tasting-longan-jujube-portrait",
    titleZh: "桂圓紅棗燕窩",
    subtitle: "一盞溫養 · 把珍貴喝成日常",
    lead: "桂圓的醇香、紅棗的甘潤,與晶瑩燕窩緩緩交融。",
    intro: [
      "看似簡單的一盞,卻蘊藏著東方養生文化的細膩智慧。",
    ],
    flavor: [
      "入口細緻柔滑,尾韻甘甜悠長。",
      "不僅是一碗甜品,更是一份對身體的溫柔款待。",
    ],
    closing: "霽雲・恆時即飲 — 把珍貴,喝成日常。",
    relatedProduct: {
      name: "霽雲・恆時即飲",
      spec: "60ml × 174 入",
      note: "全年滋養配方",
      productId: "heng_shi_ji",
    },
    tags: ["簡單", "優雅", "燕窩", "即飲", "頂級保養"],
    gallery: [
      "tasting-longan-jujube-2",
      "tasting-longan-jujube-3",
      "tasting-longan-jujube-4",
    ],
  },
};


export default function BrandTasting() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const note = slug ? TASTING_NOTES[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!note) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-dark">品味筆記不存在</h2>
          <p className="text-base text-gray-mid mt-2">請從 LINE 重新點選入口</p>
          <button onClick={() => navigate("/")} className="btn-primary mt-6 px-6">
            回首頁
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-20">
      {/* Header eyebrow */}
      <header className="bg-primary-dark text-white px-6 py-8 text-center">
        <div className="text-xs tracking-[0.3em] opacity-70 uppercase">
          霽雲 · 恆時珍藏
        </div>
        <div className="text-sm mt-2 opacity-75 tracking-widest uppercase">
          Tasting Notes
        </div>
      </header>

      {/* Hero 3:4 直幅 */}
      <img
        src={`${GCS_BASE}/${note.imageKey}.jpg`}
        alt={note.titleZh}
        className="w-full aspect-[3/4] object-cover"
      />

      {/* Title block — 受眾 50+,字級整體上調一級;裝飾性 tracking 文字保留小,內文放大 */}
      <div className="px-6 pt-8 pb-2">
        <h1 className="text-3xl font-bold text-primary-dark tracking-wide">
          {note.titleZh}
        </h1>
        <div className="text-sm text-gray-mid mt-2 tracking-wider">
          {note.subtitle}
        </div>
      </div>

      {/* Lead quote */}
      <blockquote className="mx-6 my-6 border-l-[3px] border-coral pl-5 py-1">
        <p className="text-xl leading-relaxed text-primary-dark">
          {note.lead}
        </p>
      </blockquote>

      {/* Intro */}
      <div className="px-6 space-y-4 text-lg leading-loose text-charcoal/85">
        {note.intro.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {/* Flavor description 段 */}
      <div className="px-6 mt-6 space-y-3 text-lg leading-loose text-charcoal/85">
        {note.flavor.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {/* Closing 品牌標語 */}
      <div className="px-6 mt-8">
        <p className="text-lg leading-loose text-primary-dark font-medium">
          {note.closing}
        </p>
      </div>

      {/* Related Product reference card — 有 productId 就整張卡可點,跳 /products#product-{id} */}
      {note.relatedProduct && (
        <div className="px-6 mt-8">
          <button
            type="button"
            disabled={!note.relatedProduct.productId}
            onClick={() => {
              if (note.relatedProduct?.productId) {
                navigate(`/products#product-${note.relatedProduct.productId}`);
              }
            }}
            className="w-full text-left bg-white border border-sage-light/40 rounded-lg p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03)] enabled:active:bg-cream transition-colors disabled:cursor-default"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-xs tracking-[0.3em] text-sage uppercase">
                  Related Product
                </div>
                <div className="mt-2 text-lg font-bold text-primary-dark">
                  {note.relatedProduct.name}
                </div>
                <div className="text-base text-gray-mid mt-1">
                  {note.relatedProduct.spec}
                </div>
                {note.relatedProduct.note && (
                  <div className="text-sm text-sage mt-2">
                    {note.relatedProduct.note}
                  </div>
                )}
              </div>
              {note.relatedProduct.productId && (
                <div className="text-coral text-2xl flex-shrink-0 leading-none">›</div>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Tag chips */}
      {note.tags && note.tags.length > 0 && (
        <div className="px-6 mt-8 flex flex-wrap gap-2 justify-center">
          {note.tags.map((tag) => (
            <span
              key={tag}
              className="text-sm tracking-widest text-sage px-3 py-1 border border-sage-light/60 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Gallery — 1:1 縮圖 2-col grid,點圖開新分頁 */}
      {note.gallery && note.gallery.length > 0 && (
        <div className="px-6 mt-12">
          <div className="text-xs tracking-[0.3em] text-sage uppercase text-center">
            Gallery
          </div>
          <h3 className="text-lg font-bold text-primary-dark text-center mt-2 mb-5 tracking-wide">
            一盞珍藏
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {note.gallery.map((key) => (
              <a
                key={key}
                href={`${GCS_BASE}/${key}.jpg`}
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-square overflow-hidden rounded-md bg-sage-light/20"
              >
                <img
                  src={`${GCS_BASE}/${key}.jpg`}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover active:opacity-80 transition-opacity"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="text-center py-10 mt-4">
        <div className="text-sm text-gray-mid tracking-[0.3em]">
          霽雲 · 恆時珍藏
        </div>
      </footer>
    </div>
  );
}
